import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useCatalogBrowse, useCatalogCategories, useStoreFilter, useWholesaleContext } from "@/hooks/use-wholesale";
import { useDebounce } from "@/hooks/use-debounce";
import { discountForStore, editorItemFromSnapshot, unitPriceOf, type EditorItem } from "@/lib/wholesale-items";
import { ShowroomCard } from "@/components/wholesale/showroom-card";
import {
  ActiveFilters, ALL_STORES as ALL, FilterButton, ShowroomFilterSheet, activeFilterCount, type ShowroomFilterState,
} from "@/components/wholesale/showroom-filters";
import { groupCategories } from "@/lib/category-groups";
import type { CatalogRow, CatalogSort } from "@/services/wholesale-catalog";

const PER_PAGE = 40;

const SORTS: { value: CatalogSort; label: string }[] = [
  { value: "name", label: "Naziv A–Ž" },
  { value: "newest", label: "Najnovije" },
  { value: "price_desc", label: "Skuplje prvo" },
  { value: "price_asc", label: "Jeftinije prvo" },
  { value: "stock_desc", label: "Najviše na zalihi" },
];

export function itemKey(it: { storeId: string; wcProductId: number; wcVariationId: number | null }): string {
  return `${it.storeId}:${it.wcProductId}:${it.wcVariationId ?? 0}`;
}

/** Ključ krovnog reda — mreža je na nivou proizvoda, pa React ključ ne smije zavisiti
 *  od varijacije koje na kartici više nema. */
function productKey(it: { storeId: string; wcProductId: number }): string {
  return `${it.storeId}:${it.wcProductId}`;
}

/** Izlog: mreža ploča koja se dopunjuje pri dnu. Stranice se pamte redom kojim su
 *  stigle, pa listanje unazad ne traži ponovno učitavanje; promjena filtera briše
 *  akumulaciju i vraća na prvu stranicu. */
export function ShowroomGrid({
  partnerDiscount,
  storeDiscounts,
  quantityOf,
  quantityOfLine,
  onSetQuantity,
  onOpen,
}: {
  partnerDiscount: number;
  storeDiscounts: { storeId: string; discountPct: number }[];
  /** Zbir svih varijacija proizvoda u korpi — oznaka na krovnoj kartici. */
  quantityOf: (storeId: string, wcProductId: number) => number;
  /** Količina jedne linije (proizvod ili varijacija) u korpi — brojka u stepperu. */
  quantityOfLine: (key: string) => number;
  /** Brzo dodavanje s kartice: količina ide direktno u korpu, bez otvaranja detalja. */
  onSetQuantity: (line: EditorItem, quantity: number) => void;
  onOpen: (item: CatalogRow) => void;
}) {
  const [q, setQ] = useState("");
  const { store, setStore, defaultStore } = useStoreFilter(ALL);
  const [cats, setCats] = useState<string[]>([]);
  const [inStock, setInStock] = useState(true);
  const [sort, setSort] = useState<CatalogSort>("name");
  const [page, setPage] = useState(1);
  const debounced = useDebounce(q);

  const ctx = useWholesaleContext();
  const sources = ctx.data?.sources ?? [];

  const categoriesQuery = useCatalogCategories(store === ALL ? undefined : store);
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);
  const groups = useMemo(() => groupCategories(categories), [categories]);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Promjena shopa mijenja listu kategorija: odabrani slug kojeg u novoj listi nema bi
  // nevidljivo filtrirao katalog (ni u sheetu ni u tokenima se ne bi vidio), pa otpada.
  useEffect(() => {
    if (!categoriesQuery.data) return;
    const known = new Set(categoriesQuery.data.map((c) => c.slug));
    setCats((prev) => (prev.every((s) => known.has(s)) ? prev : prev.filter((s) => known.has(s))));
  }, [categoriesQuery.data]);

  const filterState: ShowroomFilterState = { store, cats, inStock };
  function applyFilters(next: ShowroomFilterState) {
    setStore(next.store);
    setCats(next.cats);
    setInStock(next.inStock);
  }

  const filters = useMemo(
    // Slugovi su sortirani da izbor istih kategorija drugim redom ne pravi novi ključ
    // upita i ne baca akumulirane stranice.
    () => ({ q: debounced, storeId: store === ALL ? "" : store, inStock, sort, categorySlugs: [...cats].sort() }),
    [debounced, store, inStock, sort, cats],
  );
  const signature = JSON.stringify(filters);

  const { data, isFetching, isPlaceholderData, error } = useCatalogBrowse({ ...filters, page, perPage: PER_PAGE });

  const [acc, setAcc] = useState<{ signature: string; pages: CatalogRow[][] }>({ signature, pages: [] });

  useEffect(() => {
    setPage(1);
  }, [signature]);

  useEffect(() => {
    // `placeholderData` drži prethodnu stranicu dok nova putuje; upisala bi rezultate
    // starog filtera pod novim potpisom, pa se prima samo pravi odgovor.
    if (!data || isPlaceholderData) return;
    setAcc((prev) => {
      const pages = prev.signature === signature ? [...prev.pages] : [];
      pages[data.page - 1] = data.items;
      return { signature, pages };
    });
  }, [data, isPlaceholderData, signature]);

  const items = acc.signature === signature ? acc.pages.flat() : [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const hasMore = page < totalPages;

  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || isFetching) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setPage((p) => p + 1);
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, isFetching, items.length]);

  const firstLoad = items.length === 0 && isFetching;

  // Sort i pretraga ostaju kako su namješteni — „Poništi" vraća samo ono što je u sheetu,
  // a shop na zadani iz postavki.
  function resetFilters() {
    setStore(defaultStore);
    setCats([]);
    setInStock(true);
  }

  return (
    <>
      <div className="sticky top-14 z-20 flex flex-col gap-2 border-b bg-background px-3 py-2.5 sm:gap-3 sm:py-3">
        <div className="flex items-center gap-2">
          <div className="relative w-full min-w-0 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-10 pl-10 pr-10 text-base"
              placeholder="Pretraži…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              inputMode="search"
              enterKeyHint="search"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Očisti pretragu"
                className="absolute right-0 top-0 flex h-full w-10 items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Select value={sort} onValueChange={(v) => setSort(v as CatalogSort)}>
            <SelectTrigger className="h-10 w-28 shrink-0 text-xs sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <FilterButton count={activeFilterCount(filterState, groups)} onClick={() => setSheetOpen(true)} />

          <span className="ml-auto hidden shrink-0 whitespace-nowrap text-[10px] uppercase tracking-[0.08em] tabular-nums text-muted-foreground sm:block">
            {isFetching && items.length === 0 ? "Učitavanje…" : `${total.toLocaleString("bs-BA")} artikala`}
          </span>
        </div>

        <ActiveFilters state={filterState} onChange={applyFilters} sources={sources} groups={groups} onReset={resetFilters} />
      </div>

      <ShowroomFilterSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        state={filterState}
        onChange={applyFilters}
        onReset={resetFilters}
        sources={sources}
        categories={categories}
        total={total}
        counting={isFetching}
      />

      {error ? (
        <p className="p-6 text-center text-xs text-destructive">{(error as Error).message}</p>
      ) : firstLoad ? (
        <div className="grid grid-cols-2 bg-background sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-background p-0">
              <Skeleton className="aspect-square w-full" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="p-10 text-center text-xs text-muted-foreground">
          {debounced.trim() ? `Nema artikala za „${debounced.trim()}".` : "Katalog je prazan."}
          {cats.length > 0 && " Probaj bez filtera kategorija."}
          {inStock && ' Isključi „Samo na zalihi" da vidiš i rasprodano.'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 bg-background sm:grid-cols-3 lg:grid-cols-4">
            {items.map((it) => {
              const pct = discountForStore(it.storeId, partnerDiscount, storeDiscounts);
              const withDiscount = (p: number | null) =>
                unitPriceOf({ listPrice: p ?? 0, discountPct: pct, overridePrice: null });
              return (
                <ShowroomCard
                  key={productKey(it)}
                  item={it}
                  discountPct={pct}
                  unitPrice={withDiscount(it.listPrice)}
                  unitPriceMax={withDiscount(it.priceMax ?? it.listPrice)}
                  inCartQty={quantityOf(it.storeId, it.wcProductId)}
                  quantityOf={(snap) => quantityOfLine(itemKey(snap))}
                  onSetQuantity={(snap, qty) => onSetQuantity(editorItemFromSnapshot(snap, pct, Math.max(0, qty)), qty)}
                  onOpen={() => onOpen(it)}
                />
              );
            })}
          </div>

          <div ref={sentinel} className="flex items-center justify-center py-6 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : hasMore ? (
              "Nastavi listati"
            ) : (
              `Kraj kataloga · ${total.toLocaleString("bs-BA")} artikala`
            )}
          </div>
        </>
      )}
    </>
  );
}
